/**
 * Filters for module mwApp.common
 */

import moment from 'moment'
import { ENTITY_TYPES } from 'app/core/constants'

/**
 * Filter entitytype <br>
 * Uses {@link core.constant:ENTITY_TYPES} constant map to return value by key
 *
 * @class entitytype
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:entitytype
 */

/* @ngInject */
export function entitytypeFilter () {
  return function (input) {
    return ENTITY_TYPES[input]
  }
}

/**
 * Filter, returning "Add" or "Edit" depending on entity id <br>
 *
 * @class addOrEdit
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:addOrEdit
 */

/* @ngInject */
export function addOrEditFilter () {
  return function (input) {
    const viewMode = window.location.search.substring(1) === 'view'
    return input && (+input || (typeof input === 'string' && input !== 'new')) ? viewMode ? 'View' : 'Edit' : 'Add'
  }
}

/**
 * Filter, grouping array, or not <br>
 * Same as 'groupBy' filter from angular.filter module, only with one flag - do not group,
 * Used in subscribers page, when we do not need to group list by packages
 *
 * @class groupByOrNot
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:groupByOrNot
 */

/* @ngInject */
export function groupByOrNotFilter ($filter) {
  return function (collection, property, doGroup) {
    return $filter('groupBy')(collection, doGroup ? property : null)
  }
}

/**
 * Filter array by fields with OR condition, instead of standard 'filter' filter
 *
 * @class filterBy
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:filterBy
 */

/* @ngInject */
export function filterByFilter () {
  /**
   * Match object field against search string
   *
   * @param  {Object} o      - object
   * @param  {String} field  - field name, could be complex (e.g. 'childObject.name')
   * @param  {String} search - search string
   * @return {Boolean}
   */
  function match (o, field, search) {
    // simple field
    if (field.indexOf('.') === -1) {
      search = search.toLowerCase() // search to lower case
      return typeof o[field] === 'string' && o[field].toLowerCase().indexOf(search) !== -1
    }

    // complex field
    var fields = field.split('.')
    field = fields.shift()
    return o[field] && match(o[field], fields.join('.'), search) // match recursively
  }

  return function (arr, fields, search) {
    // if there is no search string -> return source array
    if (!search) {
      return arr
    }

    // if there is no source array -> return empty array
    if (!arr || !arr.length || !fields) {
      return []
    }

    // split fields by comma
    fields = fields.split(',')

    // search matched items
    var filtered = []
    for (var i = 0, l = arr.length; i < l; i++) {
      for (var j = 0, k = fields.length; j < k; j++) {
        if (match(arr[i], fields[j], search)) {
          filtered.push(arr[i])
          break
        }
      }
    }
    return filtered
  }
}

/**
 * @description checking that acl isn't enabled
 *
 * @class isACLDisable
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:isACLDisable
 */

/* @ngInject */
export function isACLDisableFilter () {
  return function (acl, acls) {
    if (Array.isArray(acls) && !acls.map(({ name }) => name).includes(acl)) {
      return 'text-decoration-line-through'
    }
  }
}

/**
 * Formats date to a string based on the requested format
 *
 * @class mmnt
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:mmnt
 */

/* @ngInject */
export function mmntFilter () {
  // http://momentjs.com/docs/#/displaying/format/
  var formats = {
    fulll: 'D MMM Y HH:mm',
    full: 'DD.MM.Y HH:mm',
    short: 'DD.MM.Y',
    date: 'D MMM Y',
    time: 'HH:mm',
    fulltime: 'HH:mm:ss'
  }

  return function (date, format) {
    if (date === null || date === undefined) {
      return date
    }

    format = format || 'full'
    const timezoneOffset = parseInt(window.localStorage.getItem('timezoneOffset'))

    // human format, used for last activity in subscribers list
    if (format === 'human') {
      var d = moment(date)
      var today = moment().startOf('day')
      var yesterday = moment().subtract(1, 'day').startOf('day')
      var tomorrow = moment().add(1, 'day').startOf('day')
      var afterTomorrow = moment().add(2, 'days').startOf('day')

      if (d.isBetween(today, tomorrow, null, '[)')) {
        return 'Today'
      }
      if (d.isBetween(yesterday, today, null, '[)')) {
        return 'Yesterday'
      }
      if (d.isBetween(tomorrow, afterTomorrow, null, '[)')) {
        return 'Tomorrow'
      }

      // else
      format = 'short'
    }
    return moment.utc(date instanceof moment ? date : moment(date)).add(timezoneOffset, 'hours')
      .format(
        formats[format]
          ? formats[format]
          : format
      )
      .replace(/\s+/g, '\u00A0') // no-break space
  }
}

/**
 * Highlight search filter
 *
 * @class highlightSearch
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:highlightSearch
 */

/* @ngInject */
export function highlightSearchFilter ($sce) {
  function escapeRegexp (query) {
    return query.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
  }

  return function (str, query, type, cls) {
    str = str ? String(str) : str
    query = query ? String(query).replace(/-/g, ' ') : query
    cls = cls || 'highlightedSearch'

    var ret = str

    if (query && str) {
      var regexpStr
      switch (type) {
        case 'phone':
          regexpStr = query.split(/\s+/).map(q => q.split('').map(escapeRegexp).join('[^\\d]*')).join('|')
          break
        case 'mac':
          regexpStr = '(' + query.replace(/[^0-9A-F]/ig, '').split('').join('.*?') + ')'
          break
        default:
          regexpStr = escapeRegexp(query).split(/\s+/).join('|')
      }

      ret = str.replace(new RegExp(regexpStr, 'gi'), '<font class="' + cls + '">$&</font>')
    }

    return $sce.trustAsHtml(ret)
  }
}

/**
 * Mask filter
 * uses https://github.com/candreoliveira/ngMask
 *      https://stackoverflow.com/questions/31793905/apply-generic-mask-via-filter
 *
 * @class mask
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:mask
 */

/* @ngInject */
export function maskFilter (MaskService) {
  return function (text, mask) {
    text = text || ''
    var maskService = MaskService.create()
    maskService.generateRegex({ mask })
    return maskService.getViewValue(text).withDivisors()
  }
}

/**
 * Nice round for numbers filter
 * `fraction` - number of decimal places to round the number to
 * Adds sign '≈' if rounded number != original value
 * See https://stackoverflow.com/questions/27850765/how-to-round-in-javascript-angular-js-but-remove-insignificant-digits
 *
 * @class niceRound
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:niceRound
 */

/* @ngInject */
export function niceRoundFilter (numberFilter) {
  return function (input, fraction) {
    const value = input || 0
    const rvalue = value.toFixed(fraction).replace(/(?:\.0+|(\.\d+?)0+)$/, '$1')
    return (String(value) !== rvalue ? '≈ ' : '') + numberFilter(rvalue)
  }
}

/**
 * Bandwidth filter
 *
 * @class bandwidth
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:bandwidth
 */

/* @ngInject */
export function bandwidthFilter () {
  return function (bytes, precision = 1) {
    const units = ['bps', 'kbps', 'mbps', 'gbps', 'tbps', 'pbps']
    if (!bytes || isNaN(parseFloat(bytes)) || !isFinite(bytes)) {
      return '-'
    }

    const number = Math.floor(Math.log(bytes) / Math.log(1024))
    const floor = Math.pow(1024, Math.floor(number))
    return (floor > 0 ? (bytes / floor).toFixed(precision) : 0) + ' ' + units[number]
  }
}

/**
 * formatRss filter
 *
 * @class formatRss
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc filter
 * @name mwApp/common.filter:formatRss
 */

/* @ngInject */
export function formatRssFilter () {
  return function (rss) {
    return rss ? ((rss / 1024.0).toFixed(1) + ' Mb') : '--'
  }
}
